import { useEffect } from 'react'
import { posthog, isPostHogEnabled } from './posthog'
import { milestoneIsNew } from './person-dedupe'

/**
 * Record a "first time" milestone as a PostHog person property.
 *
 * Uses `set_once` — PostHog only stores the first value received.
 * Safe to call every time the action happens: repeats from the same browser
 * are skipped client-side (person-dedupe.ts), and the server ignores any
 * that get through.
 *
 * No-ops when PostHog is disabled (dev default).
 */
export function setMilestone(name: string, options?: { forFlags?: boolean }) {
  if (!isPostHogEnabled()) return
  // The server keeps only the first value, but every call is still a billed
  // $set event; skip names this browser already sent for this user.
  if (!milestoneIsNew(posthog.get_distinct_id(), name)) return
  const at = new Date().toISOString()
  posthog.people.set_once({ [name]: at })
  // For a milestone a flag targets: the next flag check (often seconds later,
  // at sign-in) evaluates with this value instead of waiting for PostHog to
  // ingest the $set. Survives identify; reset() clears it.
  if (options?.forFlags) posthog.setPersonPropertiesForFlags({ [name]: at })
}

/**
 * Hook that records the first time a user visits a page.
 * Fires `set_once({ first_visited_{page}_at: timestamp })` on mount.
 */
export function useFirstPageVisit(page: string) {
  useEffect(() => {
    setMilestone(`first_visited_${page}_at`)
  }, [page])
}
